import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService, ResponseObject } from './auth.service';
import { ServiceBase } from './service.base';
import { ArtistProduct } from '../models/artistproduct.model';
import { GalleryItems } from './galleries.service';
import { ArtistProductsSummaryRequest, ConsignedProductsRequest, SyncProductsResponse } from './artistproducts.service';
import { ConsignmentCommentRequest, SyncAcceptedProduct } from '../components/artist-components/view-consignment/view-consignment.component';
import { ViewArtistProductResponse } from '../models/viewartistproductresponse.model';
import { ShopifyStoreAccount } from './shopify-service/shopify.service';

@Injectable({
  providedIn: 'root'
})
export class ConsignmentService extends ServiceBase {
  private _api = '/consignments';

  constructor(private http: HttpClient, authService: AuthService) {
    super(authService);
  }

  public IsConsignmentProposalAccepted(id: string): Observable<boolean> {
    var httpOptions = this.RequestHeaders();
    const method: string = '/isAccepted/' + id;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.get<boolean>(url, httpOptions);
  }

  // public IsConsignmentProposalPending(id: string): Observable<boolean> {
  //   var httpOptions = this.RequestHeaders();
  //   const method: string = '/isPending/' + id;
  //   const url: string = environment.BASE_API_PATH + this._api + method;
  //   return this.http.get<boolean>(url, httpOptions);
  // }

  public getConsignmentById(id: string): Observable<Consignment> {
    var httpOptions = this.RequestHeaders();
    const method: string = '/' + id;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.get<Consignment>(url, httpOptions);
  }

  public getConsignmentProductData(consignmentId: string, artistProductId: string): Observable<GalleryConsignmentProduct> {
    var httpOptions = this.RequestHeaders();
    const method: string = '/' + consignmentId + '/product/' + artistProductId;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.get<GalleryConsignmentProduct>(url, httpOptions);
  }

  public getGalleryConsignmentProducts(artistId: string | null = null, filters: ConsignedProductsRequest): Observable<GalleryConsignmentProduct[]> {
    var httpOptions = this.RequestHeaders();
    var method: string = '/gallery-products';
    if (artistId) {
      method = method + '/' + artistId; //append the artist id
    }
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<GalleryConsignmentProduct[]>(url, filters, httpOptions);
  }

  public getConnectedGalleryConsignmentProducts(galleryId: string, filter: ArtistProductsSummaryRequest): Observable<GalleryConsignmentProduct[]> {
    var httpOptions = this.RequestHeaders();
    var method: string = '/connected-gallery-products' + '/' + galleryId;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<GalleryConsignmentProduct[]>(url, filter, httpOptions);
  }

  public getConsignments(requestParam: FetchConsignmentsRequestParam): Observable<Consignment[]> {
    var httpOptions = this.RequestHeaders();
    var method: string = '/fetch';
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<Consignment[]>(url, requestParam, httpOptions);
  }

  public getConsignmentProducts(consignmentId: string): Observable<ArtistProduct[]> {
    var httpOptions = this.RequestHeaders();
    const method: string = '/' + consignmentId + '/products';
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.get<ArtistProduct[]>(url, httpOptions);
  }



  public acceptConsignment(consignmentId: string): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const method: string = `/${consignmentId}/accept`;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<ResponseObject>(url, {}, httpOptions);
  }

  public syncAcceptedConsignmentProducts(syncAcceptedProduct: SyncAcceptedProduct): Observable<SyncProductsResponse> {
    const httpOptions = this.RequestHeaders();
    const method: string = `/sync-products-to-integrated-plateforms`;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<SyncProductsResponse>(url, syncAcceptedProduct, httpOptions);
  }

  public acceptConsignmentProduct(consignmentId: string, consignmentProductRequest: ConsignmentCommentRequest): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const method: string = `/${consignmentId}/consignmentProduct/accept`;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<ResponseObject>(url, consignmentProductRequest, httpOptions);
  }

  public generatePDF(consignmentId: string): Observable<Blob> {
    var httpOptions = this.RequestHeaders();
    const method: string = '/' + consignmentId + '/generate-pdf';
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post(url, {}, { responseType: 'blob', headers: httpOptions.headers });
  }

  public createConsignment(artistId: string, galleryId: string, data: CreateConsignmentRequest): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const method: string = `/${artistId}/gallery/${galleryId}/create`;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<ResponseObject>(url, data, httpOptions);
  }

  public updateConsignment(consignmentId: string, data: UpdateConsignmentRequest): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const method: string = `/${consignmentId}/update`;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<ResponseObject>(url, data, httpOptions);
  }

  public dispatchConsignment(consignmentId: string, formData: ConsignmentCommentRequest | null = null): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();

    const method: string = `/${consignmentId}/dispatch`;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<ResponseObject>(url, formData, httpOptions);
  }
  public markProductSold(consignmentId: string, artistProductId: string, soldPrice: number, comment: string | null = null): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();

    const method: string = '/' + consignmentId + '/products/' + artistProductId + '/sell';
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<ResponseObject>(url, { SoldPrice: soldPrice, ProductSoldComment: comment }, httpOptions);
  }

  public returnProductToArtist(consignmentId: string | null, consignmentProductId: string | null, comments: string | null): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const method: string = '/' + consignmentId + '/products/' + consignmentProductId + '/return';
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<ResponseObject>(url, { Comments: comments }, httpOptions);
  }

  public saveSuggestedPriceData(artistId: string, artistProductId: string | null, data: SuggestedPriceRequest): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();

    const method: string = '/' + artistId + '/product-detail/' + artistProductId;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<ResponseObject>(url, data, httpOptions);
  }

  public saveGalleryDescriptions(artistId: string, artistProductId: string | null, data: GalleryDescriptionRequest): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const method: string = '/' + artistId + '/description/' + artistProductId;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<ResponseObject>(url, data, httpOptions);
  }


  public rejectConsignmentProposal(consignmentProposalId: string | undefined, comment: string | null): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const method: string = `/${consignmentProposalId}/accept-proposal`;
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post<ResponseObject>(url, { Comment: comment }, httpOptions);
  }
  public removeSyncedProductToIntegratedPlatforms(
    consignmentId: string,
    consignmentProductId: string,
    productId: string,
    platform: string,
    plateformAccountIds: string[]
  ): Observable<ResponseObject> {
    const url = `${environment.BASE_API_PATH}${this._api}/remove-syncd-products-to-integrated-plateforms`;
    const requestBody = {
      consignmentId,
      consignmentProductId,
      productId,
      platform,
      plateformAccountIds
    };

    return this.http.post<ResponseObject>(url, requestBody, this.RequestHeaders());
  }


  // Generate info cards PDF for selected products
  public generateInfoCardsPdf(productIds: string[]): Observable<Blob> {
    var httpOptions = this.RequestHeaders();
    const method: string = '/generate-info-cards-pdf';
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post(url, productIds, {
      responseType: 'blob',
      headers: httpOptions.headers
    });
  }

  // Generate stickers PDF for selected products
  public generateStickersPdf(productIds: string[]): Observable<Blob> {
    var httpOptions = this.RequestHeaders();
    const method: string = '/generate-stickers-pdf';
    const url: string = environment.BASE_API_PATH + this._api + method;
    return this.http.post(url, productIds, {
      responseType: 'blob',
      headers: httpOptions.headers
    });
  }


}

export class FetchConsignmentsRequestParam {
  ArtistId?: string;
  GalleryId?: string;
  Status?: string;
}

export class CreateConsignmentRequest {
  Name!: string;
  ConsignmentStatus!: string;
  SalesTaxPercentage!: number;
  GallerySaleType: string | undefined;
  Commission!: number | undefined;
  SelectedProductIds: string[] = [];
}

export class UpdateConsignmentRequest {
  Name!: string;
  ConsignmentStatus!: string;
  SalesTaxPercentage!: number;
  GallerySaleType: string | undefined;
  Commission: number | undefined;
  SelectedProductIds: string[] = [];
}

export class Consignment {
  ArtistId!: string;
  Guid!: string;
  Name!: string;
  UserName!: string;
  IsDispatched!: boolean;
  DispatchedTime!: Date;
  CreatedDated!: Date;
  ConsignmentDispatchComment?: string;
  HasGalleryAcceptedProducts!: boolean;
  GalleryAcceptanceDate?: Date;
  GalleryId!: string;
  ConsignmentStatus!: string;
  SalesTaxPercentage!: number;
  CreatedDate!: Date;
  GallerySaleType!: string | undefined;
  Commission!: number | undefined;
  IsAccepted!: boolean;
  ConsignmentProducts: ArtistProduct[] = [];  //list of products that are part of a consignment
  //helper properties
  ArtistName: string | null = null;
  ArtistPhoneNumber: string | null = null;
  Gallery?: GalleryItems | null = null;
  GalleryName: string | null = null
  GalleryPhoneNumber: string | null = null;
  NumberOfConsignmentProducts!: number;
  SelectedProductIds: string[] = [];
  ProposalHistory: ProposalHistoryResponse[] = [];
  //Additional properties
  IsShowArtistPublicProfile: boolean = false;
  IsIncludeArtistAvailableInventory: boolean = false;
  IsIncludeArtistSalesHistory: boolean = false;
  Title: string = '';
  GalleryTermsHistoryId?: string | null;
  GalleryTermsHistory?: GalleryTermsHistory | null;

}

// ✅ Sub-models
export class GalleryTermsHistory {
  Id!: string;
  GalleryId!: string;
  Gallery?: GalleryItems | null;
  PaymentTerms!: string;
  ReturnPolicy!: string;
  TermsConditions!: string;
  Version!: number;
  UpdatedDate!: Date;
  GalleryDocuments: GalleryDocument[] = [];
  Consignments: Consignment[] = [];
  Connections?: any | null;
}

export class GalleryDocument {
  Id!: number;
  Section!: number;
  DocumentName!: string;
  DocumentUrl!: string;
  GalleryTermsHistoryId!: string;
}




export class GalleryConsignmentProduct {
  ConsignmentId!: string;
  ConsignmentProductId!: string;
  ConsignmentName!: string;
  ArtistId!: string;
  ArtistName!: string;
  ConsignmentCreatedDate!: Date;
  DispatchedDate?: Date;
  Product!: ArtistProduct;
  IsProductAcceptedByGallery!: boolean;
  GalleryAcceptedDate!: Date;
  AcceptedBeforeXDays?: number | undefined;
  SoldBeforeXDays?: number | undefined;
  RetailPrice?: number;
  IsProductSold?: boolean;
  ProductSoldDate?: Date;
  ArtTypeName!: string
  ProductSoldPrice?: number;
  IsConsignedProduct?: boolean;
  Remarks!: string;
  SuggestedSalePrice!: number;
  SquarePosProductId!: string
  ShopifyProductId!: string
  WooCommerceProductId!: string
  WixProductId!: string
  SquareSpaceProductId!: string
  /*this field to store synced platform links */
  ProductSyncedOnPlatformDTO!: ProductSyncedOnPlatformDTO[];

}

export interface ProductSyncedOnPlatformDTO {
  Id: string;
  WooCommerceStoreDetail: any;
  ShopifyStoreDetailDTO: any;
  SquareSpaceSitesDetailDto: any;
  PlatformName: string;
  PlatformNameId: number;
  PlatformProductLink: string;
  PlatformProductId: string | null;
  PlatformProductVariantId: string | null;
}

export class SuggestedPriceRequest {
  SuggestedSalePrice?: number;
  Remarks?: string;
}

export class GalleryDescriptionRequest {
  GalleryShortDescription!: string;
  GalleryLongDescription!: string;
  GallerySocialMediaDescription!: string;
}

export class ConsignmentProposalRequest {
  Id!: string;
  ProposalLogId?: string;
  ArtistId!: string;
  GalleryId!: string;
  ConsignmentId?: string;
  IsProposalAccepted?: boolean;
  IsProposalRejected?: boolean;
  AcceptedDate?: Date;
  RejectedDate?: Date;
  ProposedDate?: Date;
  IntereactedDate?: Date;
  IsGalleryInteracted?: boolean;
  ArtistComment?: string;
  GalleryComment?: string;
  ArtistName?: string;
  ArtistBusinessName?: string;
  ArtistImgUrl?: string;
  GalleryUserName?: string;
  GalleryName?: string;
  GalleryBusinessName?: string;
  GalleryImgUrl?: string;
  NumberOfConsignmentProducts?: number;
  SelectedProductIds: string[] = [];
  AcceptedProductIds: string[] = [];
  ProposalInteration?: string;
  NumberOfAcceptedConsignmentProducts?: number;
  ProposalProductViewModel?: ConsignmentProposalProduct[];
  ArtistProduct?: ArtistProduct;
  // Additional properties
  IsShowArtistPublicProfile: boolean = false;
  IsIncludeArtistAvailableInventory: boolean = false;
  IsIncludeArtistSalesHistory: boolean = false;
  Title: string = '';
  Description: string = '';
}

export interface ConsignmentProposalProduct {
  Id: string | undefined;
  ConsignmentProposalId: string | null;
  ArtistProductId: string;
  IsAccpted: boolean;
  ConsignmentProposal: any

  ConsignmentProposalRequest: ConsignmentProposalRequest | null;
  ArtistProduct_Name: string;
  ArtistProduct_ArtistRevenue: number;
  ArtistProduct_ArtistId: string;
  ArtistProduct_ClayId: string | null;
  ArtistProduct_Height: number;
  ArtistProduct_ProductImgUrl: string;
  ResizedImageUrl?: string;
  ArtistProduct_SerialNumber: string;
  ArtistProduct_SeriesId: string;
  ArtistProduct_ArtTypeId: string;
  ArtistProduct_ArtTypeName: string;
  ArtistProduct_CreatedDate: Date;
  ArtistUserName?: string;

}

export class ProposalHistoryResponse {
  Id!: string;
  ArtistId!: string;
  ActionDate!: Date;
  GalleryName!: string;
  ArtistName!: string;
  Status!: boolean;
  IsProposalAccepted?: boolean;
  IsProposalRejected?: boolean;
  ArtistComments?: string;
  GalleryComments?: string;
}

export class AcceptProposalProduct {
  acceptedproductIds: string[] = [];
  Comment?: string;
}


